/**
 * 문단 내 인라인 인용 태그 [1], [2] 클릭 시 출처 사이드바를 여는 버튼
 * - response-renderer의 processCitationPattern에서 [숫자] 패턴을 이 컴포넌트로 치환
 * - 파일 출처는 확장자 배지, 웹 출처는 파비콘. 섞여 있으면 둘 다 보여준다
 */
import { Reference } from "./message-list";
import useAnswerSideBar from "@/hooks/ai-agent/use-answer-side-bar";
import useAnswerSideBarStore from "@/stores/answer-side-bar";
import type { AnswerSideBarItem } from "@/stores/answer-side-bar";
import { getSourceFavicons, getSourceKey } from "@/utils/reference";
import {
  FileBadgeStack,
  dedupeBadges,
  getFileBadge,
  type FileBadgeInfo,
} from "@/components/ui/file-type-indicator";
import FaviconImage from "./favicon-image";

const ReferenceTag = ({ references = [] }: { references: Reference[] }) => {
  const { handleAnswerSideBarOpen } = useAnswerSideBar();
  const isOpen = useAnswerSideBarStore((state) => state.isOpen);
  /** 현재 열린 출처 그룹 식별 (string: "1,2,3" 형태, 토글·강조 비교용) */
  const selectedReferenceNumber = useAnswerSideBarStore(
    (state) => state.selectedReferenceNumber
  );
  /** 이 태그가 대표하는 references의 고유 키 (다중 reference 시 "1,2,3" 형태) */
  const sourceKey = getSourceKey(references);
  const sourceFavicons = getSourceFavicons(references);
  /* 파일 출처의 확장자 배지. 같은 확장자는 하나로 합친다 */
  const fileBadges: FileBadgeInfo[] = dedupeBadges(
    references
      .map((r) => getFileBadge(r.title))
      .filter((b): b is FileBadgeInfo => b !== null)
  );

  return (
    <button
      onClick={() => {
        handleAnswerSideBarOpen(
          "answer-reference",
          references.map((r) => ({
            id: r.reference_number,
            url: r.url,
            title: r.title,
            content: r.content,
          } satisfies AnswerSideBarItem))
        );
      }}
      className={`rounded-[8px] px-2 py-1.5 h-[24px] inline-flex items-center gap-[7px] align-text-bottom ${
        isOpen && selectedReferenceNumber === sourceKey ? "bg-[#C5F7EE]" : "bg-[#F8F8F8]"
      }`}
    >
      {/* align-text-bottom: 버튼 아래를 글자 descender 선에 맞춘다. align-middle 은
          버튼 중심을 x-height 절반 위에 두어 아래가 처져 보였다 | bg: sourceKey 일치 시 강조 */}
      {fileBadges.length > 0 && <FileBadgeStack badges={fileBadges} />}
      {sourceFavicons.length > 0 && (
        <div className="flex items-center">
          {sourceFavicons.map((favicon, index) => (
            <FaviconImage
              key={`${favicon}-${index}`}
              src={favicon}
              alt={`source-${index}`}
              className={`w-5 h-5 rounded-full object-cover border-2 border-white flex-shrink-0 ${
                index > 0 ? "-ml-[6.67px]" : ""
              }`}
            />
          ))}
        </div>
      )}
      <span className="font-semibold text-[14px] leading-none text-grey-40 flex items-center justify-center">
        {references.map((r) => `[${r.reference_number}]`).join("")}
      </span>
    </button>
  );
};

export default ReferenceTag;
